import { useHandler } from "@redotech/react-util/hook";
import UploadIcon from "@redotech/redo-icons/upload-01_filled.svg";
import { FlowType } from "@redotech/redo-model/return-flow";
import {
  DiscountMatchType,
  DiscountOptionModel,
} from "@redotech/redo-model/return-flow/condition";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { DiscountState } from "@redotech/redo-return-ui/flows/return-flow/condition-types/discount";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { UploadCsvModal } from "./upload-csv-modal";

const MATCH_TYPE_OPTIONS: RadioGroupOption<DiscountMatchType>[] = [
  { id: DiscountMatchType.EXACT, label: "Exact match" },
  { id: DiscountMatchType.CONTAINS, label: "Contains" },
];

const CODE_OPTION_OPTIONS: RadioGroupOption<DiscountOptionModel>[] = [
  { id: DiscountOptionModel.ORDER, label: "All items in order" },
  {
    id: DiscountOptionModel.ITEM,
    label: "Only items with discount code applied",
  },
];

export const DiscountDetails = memo(function DiscountDetails({
  state,
  setState,
  disabled,
  flowType,
}: ConditionTypeDetailsProps<DiscountState>) {
  const [inputValue, setInputValue] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const stateRef = useRef(state);
  const setStateRef = useRef(setState);

  // Keep refs updated
  useEffect(() => {
    stateRef.current = state;
    setStateRef.current = setState;
  });

  useEffect(() => {
    if (
      flowType === FlowType.FINALIZE_RETURN ||
      flowType === FlowType.FINALIZE_CLAIM
    ) {
      setState({ ...state, codeOption: DiscountOptionModel.ORDER });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenUploadModal = useHandler(() => {
    setUploadModalOpen(true);
  });

  const handleCloseUploadModal = useHandler(() => {
    setUploadModalOpen(false);
  });

  const handleValuesUploaded = useHandler((newCodes: string[]) => {
    const currentState = stateRef.current;
    const existingCodes = currentState.codes || [];
    const uniqueCodes = [...new Set([...existingCodes, ...newCodes])];
    setStateRef.current({ ...currentState, codes: uniqueCodes });
  });

  const tags = useMemo(
    () => state.codes.map((code) => ({ text: code, color: "gray" as const })),
    [state.codes],
  );

  return (
    <>
      <RedoRadioGroup
        disabled={disabled}
        options={MATCH_TYPE_OPTIONS}
        setValue={(matchType) => setState({ ...state, matchType })}
        size="xs"
        value={state.matchType}
      />
      <RedoTagInputField
        delimiters={["newline"]}
        description="List of discount codes or titles, case-insensitive"
        inputValue={inputValue}
        label="Discount codes/titles"
        placeholder="Type and press Enter to add"
        setInputValue={setInputValue}
        setTags={(codes) => setState({ ...state, codes })}
        size="sm"
        state={disabled ? "disabled" : "default"}
        tags={tags}
      />
      <Flex dir="column" gap="sm">
        <RedoButton
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={UploadIcon}
          onClick={handleOpenUploadModal}
          size="sm"
          type="button"
        >
          Upload CSV
        </RedoButton>
      </Flex>
      {flowType !== FlowType.FINALIZE_RETURN &&
        flowType !== FlowType.FINALIZE_CLAIM && (
          <RedoRadioGroup
            disabled={disabled}
            label="Condition is applied to"
            options={CODE_OPTION_OPTIONS}
            setValue={(codeOption) => setState({ ...state, codeOption })}
            size="xs"
            value={state.codeOption}
          />
        )}
      {uploadModalOpen && (
        <UploadCsvModal
          onClose={handleCloseUploadModal}
          onValuesUploaded={handleValuesUploaded}
          open={uploadModalOpen}
          possibleColumnNames={[
            "discount_code",
            "code",
            "discount",
            "title",
            "discount_title",
          ]}
          valueLabel="discount codes"
        />
      )}
    </>
  );
});
